import { LanSettings } from 'types/common';

declare global {
    interface Config {
        API_BASE_URL: string;
        STAGE: string;
        REGISTRATION_OPEN: string;
        EVENT_TIME: string;
        COUNTDOWNCLOCK: string;
        KEYCLOAK_URL: string;
        KEYCLOAK_REALM: string;
        KEYCLOAK_CLIENT_ID: string;
        KEYCLOAK_CLIENT_SECRET: string;
        KEYCLOAK_AUDIENCE: string;
        KEYCLOAK_SCOPE: string;
        USE_MOCK_API: string;
        DEMO_API_URL: string;
        DEMO_FILES_BASE_PATH: string;
    }
}

const dev: Config = {
    API_BASE_URL: import.meta.env.VITE_API_URL ?? 'http://localhost:8080/api',
    STAGE: import.meta.env.VITE_STAGE ?? 'test',
    REGISTRATION_OPEN: 'true',
    EVENT_TIME: '21.-24.01.2027',
    COUNTDOWNCLOCK: '2027-01-21',
    KEYCLOAK_URL: import.meta.env.VITE_KEYCLOAK_URL ?? '',
    KEYCLOAK_REALM: import.meta.env.VITE_KEYCLOAK_REALM ?? 'lanbooking',
    KEYCLOAK_CLIENT_ID: import.meta.env.VITE_KEYCLOAK_CLIENT_ID ?? 'lanbooking-frontend',
    KEYCLOAK_CLIENT_SECRET: import.meta.env.VITE_KEYCLOAK_CLIENT_SECRET ?? '',
    KEYCLOAK_AUDIENCE: import.meta.env.VITE_KEYCLOAK_AUDIENCE ?? 'lanbooking-api',
    KEYCLOAK_SCOPE: import.meta.env.VITE_KEYCLOAK_SCOPE ?? 'openid profile email',
    USE_MOCK_API: import.meta.env.VITE_USE_MOCK_API ?? 'true',
    DEMO_API_URL: import.meta.env.VITE_DEMO_API_URL ?? '/mock/demo-matches.json',
    DEMO_FILES_BASE_PATH: import.meta.env.VITE_DEMO_FILES_BASE_PATH ?? '/demos'
};

const prod: Config = {
    API_BASE_URL: import.meta.env.VITE_API_URL ?? 'https://tammilan.it/api',
    STAGE: import.meta.env.VITE_STAGE ?? 'production',
    REGISTRATION_OPEN: import.meta.env.VITE_REGISTRATION_OPEN ?? 'true',
    EVENT_TIME: import.meta.env.VITE_EVENT_TIME ?? '',
    COUNTDOWNCLOCK: import.meta.env.VITE_COUNTDOWNCLOCK ?? '2027-01-21',
    KEYCLOAK_URL: import.meta.env.VITE_KEYCLOAK_URL ?? '',
    KEYCLOAK_REALM: import.meta.env.VITE_KEYCLOAK_REALM ?? 'lanbooking',
    KEYCLOAK_CLIENT_ID: import.meta.env.VITE_KEYCLOAK_CLIENT_ID ?? 'lanbooking-frontend',
    KEYCLOAK_CLIENT_SECRET: import.meta.env.VITE_KEYCLOAK_CLIENT_SECRET ?? '',
    KEYCLOAK_AUDIENCE: import.meta.env.VITE_KEYCLOAK_AUDIENCE ?? 'lanbooking-api',
    KEYCLOAK_SCOPE: import.meta.env.VITE_KEYCLOAK_SCOPE ?? 'openid profile email',
    USE_MOCK_API: import.meta.env.VITE_USE_MOCK_API ?? 'false',
    DEMO_API_URL: import.meta.env.VITE_DEMO_API_URL ?? '/mock/demo-matches.json',
    DEMO_FILES_BASE_PATH: import.meta.env.VITE_DEMO_FILES_BASE_PATH ?? '/demos'
};

const baseConfig = import.meta.env.DEV ? dev : prod;

console.log(baseConfig);

/** Prefer an explicit API boolean, then a 'true'/'false' env override, then the default. */
export const resolveBooleanSetting = (
    apiValue: boolean | null | undefined,
    envValue: string | undefined,
    fallback: boolean
): boolean => {
    if (typeof apiValue === 'boolean') {
        return apiValue;
    }
    if (envValue === 'true' || envValue === 'false') {
        return envValue === 'true';
    }
    return fallback;
};

/**
 * Merge API settings with system overrides (API values win, then env, then defaults)
 */
export const mergeSettings = (apiSettings: LanSettings | null): LanSettings => {
    const defaults: LanSettings = {
        total: 63,
        startDate: baseConfig.COUNTDOWNCLOCK,
        endDate: baseConfig.COUNTDOWNCLOCK,
        eventName: 'Tammilanit',
        registrationEnabled: baseConfig.REGISTRATION_OPEN === 'true',
        bookingEnabled: true,
        // Branding defaults
        organizerName: 'Tammilanit-yhteisö',
        venueName: 'Porin helluntaikirkko',
        venueAddress: 'Itsenäisyydenkatu 9, 28100 Pori',
        logoPath: 'assets/Tammilanit 2026-logo.png',
        linksWebsite: 'https://tammilan.it',
        // Pricing defaults
        pricingStandardSeatPrice: 20,
        pricingPremiumSeatPrice: 30,
        pricingStandardSeatDimensions: '100x75cm',
        pricingPremiumSeatDimensions: '120x80cm',
        pricingStandardSeatLabel: 'Peruspaikka',
        pricingPremiumSeatLabel: 'Siunattu paikka',
        // Payment defaults
        paymentMobilePayNumber: '+358 45 346 2128',
        paymentBankAccount: 'FI41 1807 2961 1628 14',
        paymentBankAccountHolder: 'Riku Kuusisto'
    };

    if (!apiSettings) {
        return defaults;
    }

    return {
        total: apiSettings.total ?? defaults.total,
        startDate: apiSettings.startDate ?? import.meta.env.VITE_START_DATE ?? defaults.startDate,
        endDate: apiSettings.endDate ?? import.meta.env.VITE_END_DATE ?? defaults.endDate,
        eventName: apiSettings.eventName ?? import.meta.env.VITE_EVENT_NAME ?? defaults.eventName,
        registrationEnabled: resolveBooleanSetting(
            apiSettings.registrationEnabled,
            import.meta.env.VITE_REGISTRATION_OPEN,
            defaults.registrationEnabled
        ),
        bookingEnabled: resolveBooleanSetting(apiSettings.bookingEnabled, undefined, defaults.bookingEnabled ?? true),
        attendancePerDayEnabled: apiSettings.attendancePerDayEnabled ?? false,
        foodEnabled: apiSettings.foodEnabled ?? true,
        // Branding fields
        organizerName: apiSettings.organizerName ?? defaults.organizerName,
        venueName: apiSettings.venueName ?? defaults.venueName,
        venueAddress: apiSettings.venueAddress ?? defaults.venueAddress,
        logoPath: apiSettings.logoPath ?? defaults.logoPath,
        linksWebsite: apiSettings.linksWebsite ?? defaults.linksWebsite,
        // Pricing fields
        pricingStandardSeatPrice: apiSettings.pricingStandardSeatPrice ?? defaults.pricingStandardSeatPrice,
        pricingPremiumSeatPrice: apiSettings.pricingPremiumSeatPrice ?? defaults.pricingPremiumSeatPrice,
        pricingStandardSeatDimensions: apiSettings.pricingStandardSeatDimensions ?? defaults.pricingStandardSeatDimensions,
        pricingPremiumSeatDimensions: apiSettings.pricingPremiumSeatDimensions ?? defaults.pricingPremiumSeatDimensions,
        pricingStandardSeatLabel: apiSettings.pricingStandardSeatLabel ?? defaults.pricingStandardSeatLabel,
        pricingPremiumSeatLabel: apiSettings.pricingPremiumSeatLabel ?? defaults.pricingPremiumSeatLabel,
        // Payment fields
        paymentMobilePayNumber: apiSettings.paymentMobilePayNumber ?? defaults.paymentMobilePayNumber,
        paymentBankAccount: apiSettings.paymentBankAccount ?? defaults.paymentBankAccount,
        paymentBankAccountHolder: apiSettings.paymentBankAccountHolder ?? defaults.paymentBankAccountHolder,
        contacts: apiSettings.contacts ?? defaults.contacts
    };
};

export default {
    ...baseConfig
};
